// ============================================================================
// Vertrags-Gerüst — weitere Typen; Word-Muster separat versioniert
// ----------------------------------------------------------------------------
// Polar: VERBOTEN, diese Texte als ARKORBIT-AGB / Rechtsprodukt / Server-Standard
// auszugeben. ERLAUBT: Wizard-Slots + Anzeige „ARKO-Vorlage".
// Word-Rohquelle liegt unter data/contract-templates/phillip-2026-09/. Engine bleibt Mietvertrag.
// ============================================================================

import type { ContractSchema, ContractTemplateDef, ContractValues } from './contractTemplates.ts';

export const MUSTER_FOLGT =
  'Leerer Slot — Kanzlei-Text einfügen (kein ARKORBIT-Standard).';

/** Portal-Anzeige. Kein Personenname, kein Public-Download, kein Engine-Master. */
export const PHILLIP_MUSTER_LABEL = 'ARKO-Vorlage';

function phillipMuster(file: string): { label: string; repoPath: string } {
  return {
    label: PHILLIP_MUSTER_LABEL,
    repoPath: `data/contract-templates/phillip-2026-09/${file}`,
  };
}

type PartyExtra = 'fn' | 'geburtsdatum' | 'svnr' | 'geburtsdatum_oder_fn';

function party(
  title: string,
  nameTitle: string,
  addrTitle: string,
  extras: PartyExtra[] = [],
): ContractSchema['properties'][string] {
  const properties: NonNullable<ContractSchema['properties'][string]['properties']> = {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 200,
      title: nameTitle,
      description: 'Name bzw. Firmenwortlaut. Nur Platzhalter, kein Mustervertrag.',
    },
    anschrift: {
      type: 'string',
      maxLength: 300,
      title: addrTitle,
      description: 'Zustelladresse (Straße, Hausnummer, PLZ, Ort).',
    },
  };
  if (extras.includes('fn')) {
    properties.fn = {
      type: 'string',
      maxLength: 40,
      title: 'Firmenbuchnummer (FN)',
      description: 'Leer lassen, wenn nicht zutreffend.',
    };
  }
  if (extras.includes('geburtsdatum')) {
    properties.geburtsdatum = {
      type: 'string',
      format: 'date',
      title: 'Geburtsdatum',
      description: 'Leer lassen, wenn nicht zutreffend.',
    };
  }
  if (extras.includes('svnr')) {
    properties.svnr = {
      type: 'string',
      maxLength: 20,
      title: 'Sozialversicherungsnummer',
      description: 'Leer lassen, wenn nicht zutreffend. Keine echten Mandantendaten.',
    };
  }
  if (extras.includes('geburtsdatum_oder_fn')) {
    properties.geburtsdatum_oder_fn = {
      type: 'string',
      maxLength: 80,
      title: 'Geburtsdatum / FN',
      description: 'Geburtsdatum oder Firmenbuchnummer. Leer lassen, wenn nicht zutreffend.',
    };
  }
  return {
    type: 'object',
    title,
    description: 'Partei des Entwurfs (nur fiktive Testdaten).',
    required: ['name'],
    properties,
  };
}

/** extraValues-Key: A+B Start, Plus addiert C, D, … Kein Phillip-Fließtext. */
export const GESELLSCHAFTER_LIST_KEY = 'gesellschafter_liste';
export const GESELLSCHAFTER_MIN_COUNT = 1;
export const GESELLSCHAFTER_START_COUNT = 2;
export const GESELLSCHAFTER_SCHEMA_KEYS = [
  'gesellschafter_a',
  'gesellschafter_b',
  'anteil_a_prozent',
  'anteil_b_prozent',
] as const;

export type GesellschafterListeEintrag = {
  name: string;
  anschrift: string;
  anteil_prozent: string;
};

export function gesellschafterLetter(index: number): string {
  if (index < 26) return String.fromCharCode(65 + index);
  return String(index + 1);
}

export function emptyGesellschafterEintrag(): GesellschafterListeEintrag {
  return { name: '', anschrift: '', anteil_prozent: '' };
}

export function isGesellschafterSchemaKey(key: string): boolean {
  return (GESELLSCHAFTER_SCHEMA_KEYS as readonly string[]).includes(key);
}

function partyNameAddr(v: unknown): { name: string; anschrift: string } {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return { name: '', anschrift: '' };
  const o = v as Record<string, unknown>;
  return {
    name: typeof o.name === 'string' ? o.name : '',
    anschrift: typeof o.anschrift === 'string' ? o.anschrift : '',
  };
}

function anteilAsText(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'string') return v;
  return '';
}

function normalizeGesellschafterEintrag(v: unknown): GesellschafterListeEintrag {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return emptyGesellschafterEintrag();
  const o = v as Record<string, unknown>;
  return {
    name: typeof o.name === 'string' ? o.name : '',
    anschrift: typeof o.anschrift === 'string' ? o.anschrift : '',
    anteil_prozent: anteilAsText(o.anteil_prozent),
  };
}

/** Liste aus extraValues, sonst A+B aus dem Formular (alte Drafts). Mindestens Start A+B wenn nichts da. */
export function resolveGesellschafterListe(
  form: ContractValues,
  extra: Record<string, unknown>,
): GesellschafterListeEintrag[] {
  const raw = extra[GESELLSCHAFTER_LIST_KEY];
  if (Array.isArray(raw) && raw.length >= GESELLSCHAFTER_MIN_COUNT) {
    return raw.map(normalizeGesellschafterEintrag);
  }
  const a = partyNameAddr(form.gesellschafter_a);
  const b = partyNameAddr(form.gesellschafter_b);
  return [
    { ...a, anteil_prozent: anteilAsText(form.anteil_a_prozent) },
    { ...b, anteil_prozent: anteilAsText(form.anteil_b_prozent) },
  ];
}

export function syncGesellschafterForm(
  form: ContractValues,
  liste: GesellschafterListeEintrag[],
): ContractValues {
  const a = liste[0] || emptyGesellschafterEintrag();
  const b = liste[1] || emptyGesellschafterEintrag();
  return {
    ...form,
    gesellschafter_a: { name: a.name, anschrift: a.anschrift },
    gesellschafter_b: { name: b.name, anschrift: b.anschrift },
    anteil_a_prozent: a.anteil_prozent,
    anteil_b_prozent: b.anteil_prozent,
  };
}

const KAUF_SCHEMA: ContractSchema = {
  title: 'Kauf / Verkauf — Gerüst',
  description:
    'ARKO-Vorlage für bewegliche Güter. Liegenschaft bleibt Slot ohne erfundene Klauseln. Kein ARKORBIT-AGB.',
  type: 'object',
  required: ['gegenstand_art', 'verkaeufer', 'kaeufer', 'gegenstand'],
  properties: {
    gegenstand_art: {
      type: 'string',
      enum: ['Liegenschaft', 'Bewegliche Güter'],
      title: 'Kaufgegenstand',
      description: 'Liegenschaft: nur Lage-Slot. Bewegliche Güter: Muster-Klauseln.',
    },
    verkaeufer: party('Verkäufer', 'Name des Verkäufers', 'Anschrift des Verkäufers', [
      'geburtsdatum_oder_fn',
    ]),
    kaeufer: party('Käufer', 'Name des Käufers', 'Anschrift des Käufers', ['geburtsdatum_oder_fn']),
    gegenstand: {
      type: 'object',
      title: 'Gegenstand',
      required: ['bezeichnung'],
      properties: {
        bezeichnung: {
          type: 'string',
          minLength: 1,
          maxLength: 300,
          title: 'Bezeichnung',
          description: 'Kurzbezeichnung des Kaufgegenstands.',
        },
        beschreibung: {
          type: 'string',
          maxLength: 500,
          format: 'textarea',
          title: 'Beschreibung',
          description: 'Leer lassen, wenn nicht benötigt.',
        },
        lage_oder_beschreibung: {
          type: 'string',
          maxLength: 500,
          format: 'textarea',
          title: 'Lage / EZ (Liegenschaft)',
          description: 'Nur bei Liegenschaft: Adresse oder EZ/KG. Kein erfundener Liegenschafts-Kauftext.',
        },
        identifikation: {
          type: 'string',
          maxLength: 120,
          title: 'Seriennummer / Identifikation',
          description: 'Leer lassen, wenn nicht vorhanden.',
        },
        zustand: {
          type: 'string',
          maxLength: 300,
          title: 'Zustand',
          description: 'Leer lassen, wenn nicht festgehalten.',
        },
      },
    },
    kaufpreis_eur: {
      type: 'number',
      minimum: 0,
      title: 'Kaufpreis (EUR)',
      description: 'Zahlenfeld wie beim Mietvertrag.',
    },
    kaufpreis_worten: {
      type: 'string',
      maxLength: 200,
      title: 'Kaufpreis in Worten',
      description: 'Ohne Währungsbezeichnung. Leer lassen, wenn nicht ausgefüllt.',
    },
    zahlungsart: {
      type: 'string',
      enum: ['bei Vertragsunterzeichnung', 'bei Übergabe', 'bis Stichtag', 'Überweisung'],
      title: 'Zahlung',
    },
    zahlung_bis: {
      type: 'string',
      format: 'date',
      title: 'Zahlung bis',
      description: 'Nur bei „bis Stichtag“.',
    },
    iban: {
      type: 'string',
      maxLength: 34,
      title: 'IBAN',
      description: 'Nur bei Überweisung. Fiktive Testdaten.',
    },
    uebergabe_am: {
      type: 'string',
      format: 'date',
      title: 'Übergabe am',
    },
    uebergabe_ort: {
      type: 'string',
      maxLength: 200,
      title: 'Übergabeort',
    },
    gewaehrleistung_variante: {
      type: 'string',
      enum: ['Privatverkauf', 'Unternehmergeschäft'],
      title: 'Gewährleistung',
      description: 'Zwei Varianten — keine dritte erfinden.',
    },
    kosten_tragung: {
      type: 'string',
      enum: ['der Käufer', 'der Verkäufer', 'beide Parteien je zur Hälfte'],
      title: 'Kosten und Abgaben',
    },
    ort: { type: 'string', maxLength: 80, title: 'Ort' },
    datum: { type: 'string', format: 'date', title: 'Datum' },
  },
};

const GESELLSCHAFTER_SCHEMA: ContractSchema = {
  title: 'Gesellschaftervertrag — Gerüst',
  description: MUSTER_FOLGT,
  type: 'object',
  required: ['gesellschaft', 'gesellschafter_a'],
  properties: {
    gesellschaft: {
      type: 'object',
      title: 'Gesellschaft',
      required: ['name', 'rechtsform'],
      properties: {
        name: {
          type: 'string',
          minLength: 1,
          maxLength: 200,
          title: 'Firmenwortlaut',
          description: 'Name der Gesellschaft (Slot).',
        },
        rechtsform: {
          type: 'string',
          enum: ['GmbH', 'OG', 'KG', 'FlexKap-GmbH', 'GesbR', 'Sonstiges'],
          title: 'Rechtsform',
          description: 'Wahl-Option, kein Gesellschaftsvertragstext.',
        },
      },
    },
    stammkapital_eur: {
      type: 'number',
      minimum: 0,
      title: 'Stammkapital / Einlagen (EUR)',
      description: 'Optionaler Zahlen-Slot.',
    },
    gesellschafter_a: party('Gesellschafter A', 'Name / Firma', 'Anschrift'),
    gesellschafter_b: party('Gesellschafter B', 'Name / Firma', 'Anschrift'),
    anteil_a_prozent: {
      type: 'number',
      minimum: 0,
      maximum: 100,
      title: 'Anteil A (%)',
      description: 'Optional.',
    },
    anteil_b_prozent: {
      type: 'number',
      minimum: 0,
      maximum: 100,
      title: 'Anteil B (%)',
      description: 'Optional.',
    },
    geschaeftsfuehrung: {
      type: 'string',
      maxLength: 400,
      format: 'textarea',
      title: 'Geschäftsführung (Stichwort)',
      description: MUSTER_FOLGT,
    },
  },
};

const INNEN_SCHEMA: ContractSchema = {
  title: 'Innenverhältnis-Erklärung — Gerüst',
  description: MUSTER_FOLGT,
  type: 'object',
  required: ['erklaerende', 'gegenstand'],
  properties: {
    erklaerende: party('Erklärende Partei', 'Name', 'Anschrift'),
    gegenpartei: party('Gegenpartei / Mitgesellschafter', 'Name', 'Anschrift'),
    gegenstand: {
      type: 'string',
      minLength: 1,
      maxLength: 400,
      title: 'Gegenstand der Erklärung',
      description: 'Stichwort (z. B. Stimmbindung) — kein vorformulierter Text.',
    },
    wirksam_ab: {
      type: 'string',
      format: 'date',
      title: 'Wirksam ab',
      description: 'Optionales Datum.',
    },
  },
};

const PFAND_LEIH_SCHEMA: ContractSchema = {
  title: 'Pfand- / Leihvertrag — Gerüst',
  description:
    'ARKO-Vorlage für Faustpfand. Leihe bleibt Slot ohne erfundene Klauseln. Kein ARKORBIT-AGB.',
  type: 'object',
  required: ['vertragstyp', 'geber', 'nehmer', 'gegenstand'],
  properties: {
    vertragstyp: {
      type: 'string',
      enum: ['Pfandvertrag', 'Leihvertrag'],
      title: 'Vertragsart',
      description: 'Pfand = Muster-Klauseln. Leihe = nur Slots.',
    },
    geber: party('Pfandgläubiger / Verleiher', 'Name', 'Anschrift', ['geburtsdatum_oder_fn']),
    nehmer: party('Pfandgeber / Entleiher', 'Name', 'Anschrift', ['geburtsdatum_oder_fn']),
    rechtsgrund: {
      type: 'string',
      maxLength: 300,
      title: 'Gesicherte Forderung — Rechtsgrund',
      description: 'Leerer Slot, kein erfundener Forderungstext.',
    },
    schuldner: {
      type: 'string',
      maxLength: 200,
      title: 'Schuldner der gesicherten Forderung',
    },
    forderung_eur: {
      type: 'number',
      minimum: 0,
      title: 'Forderungshöhe (EUR)',
    },
    gegenstand: {
      type: 'string',
      minLength: 1,
      maxLength: 400,
      title: 'Pfandgegenstand / Sache',
    },
    gegenstand_beschreibung: {
      type: 'string',
      maxLength: 500,
      format: 'textarea',
      title: 'Beschreibung',
    },
    identifikation: {
      type: 'string',
      maxLength: 120,
      title: 'Seriennummer / Identifikation',
    },
    schaetzwert_eur: {
      type: 'number',
      minimum: 0,
      title: 'Geschätzter Wert (EUR)',
    },
    beginn: {
      type: 'string',
      format: 'date',
      title: 'Übergabe / Beginn',
    },
    befristet: {
      type: 'boolean',
      title: 'Befristet?',
      description: 'Vor allem Leihe. Pfand endet mit Tilgung.',
    },
    ende: {
      type: 'string',
      format: 'date',
      title: 'Ende',
      description: 'Nur bei Befristung.',
    },
    kosten_tragung: {
      type: 'string',
      enum: ['der Pfandgeber', 'der Schuldner', 'der Pfandgläubiger', 'nach gesetzlicher Regelung'],
      title: 'Kosten trägt',
    },
    ort: { type: 'string', maxLength: 80, title: 'Ort' },
    datum: { type: 'string', format: 'date', title: 'Datum' },
  },
  if: { properties: { befristet: { const: true } }, required: ['befristet'] },
  then: { required: ['ende'] },
};

const SERVITUT_SCHEMA: ContractSchema = {
  title: 'Servitut- / Räumungsvertrag — Gerüst',
  description: MUSTER_FOLGT,
  type: 'object',
  required: ['vertragstyp', 'berechtigte', 'verpflichtete', 'liegenschaft'],
  properties: {
    vertragstyp: {
      type: 'string',
      enum: ['Servitut', 'Räumungsvertrag'],
      title: 'Vertragsart',
      description: 'Genauere Wahl zwischen Servitut und Räumung.',
    },
    berechtigte: party('Berechtigte Partei', 'Name', 'Anschrift'),
    verpflichtete: party('Verpflichtete Partei', 'Name', 'Anschrift'),
    liegenschaft: {
      type: 'object',
      title: 'Liegenschaft',
      required: ['adresse'],
      properties: {
        adresse: {
          type: 'string',
          minLength: 1,
          maxLength: 300,
          title: 'Adresse / Lage',
        },
        ez_kg: {
          type: 'string',
          maxLength: 80,
          title: 'EZ / KG (optional)',
          description: 'Grundbuch-Slot, kein Mustertext.',
        },
      },
    },
  },
};

const ARBEIT_SCHEMA: ContractSchema = {
  title: 'Arbeitsvertrag — Gerüst',
  description:
    'ARKO-Vorlage. Probezeit, KV, Freistellung und Überstunden-Klausel sind optionale Bausteine. Dokumentation und Gegenzeichnung gehören zur Klausel, nicht als Extra-Chip. Kein Engine-docx, kein ARKORBIT-AGB.',
  type: 'object',
  required: ['dienstgeber', 'dienstnehmer', 'beginn', 'position'],
  properties: {
    dienstgeber: party('Dienstgeber', 'Name / Firma', 'Anschrift', ['fn']),
    dienstnehmer: party('Dienstnehmer', 'Name', 'Anschrift', ['geburtsdatum', 'svnr']),
    beginn: {
      type: 'string',
      format: 'date',
      title: 'Beginn',
    },
    befristet: {
      type: 'boolean',
      title: 'Befristet?',
    },
    befristung_bis: {
      type: 'string',
      format: 'date',
      title: 'Befristet bis',
      description: 'Nur bei Befristung.',
    },
    position: {
      type: 'string',
      minLength: 1,
      maxLength: 200,
      title: 'Tätigkeit / Position',
    },
    aufgaben: {
      type: 'string',
      maxLength: 800,
      format: 'textarea',
      title: 'Aufgaben (Stichpunkte)',
      description: 'Leerer Slot — keine erfundenen Aufgaben.',
    },
    dienstort: {
      type: 'string',
      maxLength: 300,
      title: 'Dienstort',
    },
    wochenstunden: {
      type: 'number',
      minimum: 0,
      title: 'Normalarbeitszeit (Stunden/Woche)',
    },
    brutto_eur: {
      type: 'number',
      minimum: 0,
      title: 'Bruttogehalt (EUR / Monat)',
    },
    zahlungstermin: {
      type: 'string',
      maxLength: 80,
      title: 'Zahlungstermin',
      description: 'z. B. am Monatsende',
    },
    iban: {
      type: 'string',
      maxLength: 34,
      title: 'IBAN (Gehalt)',
      description: 'Fiktive Testdaten.',
    },
    kollektivvertrag: {
      type: 'string',
      maxLength: 200,
      title: 'Kollektivvertrag (Bezeichnung)',
    },
    kv_vollzeit_brutto: {
      type: 'number',
      minimum: 0,
      title: 'KV Vollzeit brutto (EUR)',
      description: 'Nur mit Baustein Überzahlung.',
    },
    ueberzahlung_eur: {
      type: 'number',
      minimum: 0,
      title: 'Überzahlung (EUR)',
    },
    probezeit_dauer: {
      type: 'string',
      maxLength: 80,
      title: 'Probezeit (Dauer)',
      description: 'z. B. 1 Monat — leerer Slot, kein Pflichtvolltext.',
    },
    kuendigungsfrist_monate: {
      type: 'number',
      minimum: 0,
      title: 'Kündigungsfrist (Monate)',
      description: 'Leer lassen, wenn der Baustein aus ist.',
    },
    ort_datum: {
      type: 'string',
      maxLength: 80,
      title: 'Ort, Datum',
    },
  },
  if: { properties: { befristet: { const: true } }, required: ['befristet'] },
  then: { required: ['befristung_bis'] },
};

const DARLEHEN_SCHEMA: ContractSchema = {
  title: 'Darlehensvertrag — Gerüst',
  description: 'ARKO-Vorlage. Slots im Wizard-Stil. Kein ARKORBIT-AGB.',
  type: 'object',
  required: ['darlehensgeber', 'darlehensnehmer', 'betrag_eur'],
  properties: {
    darlehensgeber: party('Darlehensgeber', 'Name / Firma', 'Anschrift', ['geburtsdatum_oder_fn']),
    darlehensnehmer: party('Darlehensnehmer', 'Name / Firma', 'Anschrift', ['geburtsdatum_oder_fn']),
    waehrung: {
      type: 'string',
      enum: ['EUR', 'USD', 'CHF', 'sonstige'],
      title: 'Währung',
      description: 'Default-Slot EUR; andere Währung nur wenn gewählt.',
    },
    betrag_eur: {
      type: 'number',
      minimum: 0,
      title: 'Darlehensbetrag',
    },
    betrag_worten: {
      type: 'string',
      maxLength: 200,
      title: 'Betrag in Worten',
      description: 'Ohne Währungsbezeichnung (die steht im Währungsfeld).',
    },
    auszahlungsart: {
      type: 'string',
      enum: ['Überweisung', 'Barauszahlung'],
      title: 'Auszahlung',
    },
    auszahlung_bis: {
      type: 'string',
      format: 'date',
      title: 'Auszahlung bis',
    },
    iban: {
      type: 'string',
      maxLength: 34,
      title: 'IBAN (Auszahlung)',
    },
    laufzeit_von: {
      type: 'string',
      format: 'date',
      title: 'Laufzeit von',
    },
    laufzeit_bis: {
      type: 'string',
      format: 'date',
      title: 'Laufzeit bis',
    },
    verzinsung: {
      type: 'string',
      enum: ['unverzinslich', 'Zinssatz % p.a.', 'Index / Inflation'],
      title: 'Verzinsung',
    },
    zinssatz_prozent: {
      type: 'number',
      minimum: 0,
      title: 'Zinssatz (% p.a.)',
      description: 'Nur bei Zinssatz % p.a.',
    },
    index_notiz: {
      type: 'string',
      maxLength: 200,
      title: 'Index / Inflation',
      description: 'Leerer Slot — keine erfundene Indexformel.',
    },
    zins_faelligkeit: {
      type: 'string',
      enum: ['monatlich', 'vierteljährlich', 'jährlich', 'gemeinsam mit der Tilgungsleistung'],
      title: 'Zinsfälligkeit',
    },
    rueckzahlung: {
      type: 'string',
      enum: ['Einmalzahlung', 'Raten'],
      title: 'Rückzahlung',
    },
    raten_anzahl: { type: 'number', minimum: 0, title: 'Anzahl der Raten' },
    rate_eur: { type: 'number', minimum: 0, title: 'Höhe je Rate' },
    rate_faelligkeit: {
      type: 'string',
      maxLength: 40,
      title: 'Ratentag im Monat',
      description: 'z. B. 1. oder 15.',
    },
    rueck_iban: { type: 'string', maxLength: 34, title: 'IBAN (Rückzahlung)' },
    sicherheit_art: {
      type: 'string',
      enum: ['keine', 'Bürgschaft', 'Pfandrecht', 'sonstige Sicherheit'],
      title: 'Sicherheit',
    },
    sicherheit_notiz: {
      type: 'string',
      maxLength: 300,
      format: 'textarea',
      title: 'Sonstige Sicherheit',
      description: 'Leerer Slot, wenn „sonstige Sicherheit“.',
    },
    ort: { type: 'string', maxLength: 80, title: 'Ort' },
    datum: { type: 'string', format: 'date', title: 'Datum' },
  },
};

const LIZENZ_SCHEMA: ContractSchema = {
  title: 'Lizenzvertrag — Gerüst',
  description: 'ARKO-Vorlage. Leere Slots statt XXXX. Kein ARKORBIT-AGB.',
  type: 'object',
  required: ['lizenzgeber', 'lizenznehmer', 'gegenstand'],
  properties: {
    lizenzgeber: party('Lizenzgeber', 'Name / Firma', 'Anschrift', ['fn']),
    lizenznehmer: party('Lizenznehmer', 'Name / Firma', 'Anschrift', ['fn']),
    gegenstand: {
      type: 'string',
      minLength: 1,
      maxLength: 400,
      format: 'textarea',
      title: 'Lizenzgegenstand',
      description: 'Leerer Slot, kein XXXX.',
    },
    art: {
      type: 'string',
      enum: ['ausschließliche (exklusive) Lizenz', 'nicht ausschließliche (einfache) Lizenz'],
      title: 'Art der Lizenz',
    },
    nutzungsumfang: {
      type: 'array',
      title: 'Umfang der Nutzungsrechte',
      items: {
        type: 'string',
        enum: [
          'Vervielfältigung',
          'Verbreitung',
          'öffentliche Wiedergabe',
          'Bearbeitung',
          'Veröffentlichung',
          'Softwarebetrieb',
          'sonstige Nutzung',
        ],
      },
    },
    zwecke: {
      type: 'string',
      maxLength: 400,
      format: 'textarea',
      title: 'Zwecke der Nutzung',
      description: 'Leerer Slot.',
    },
    gebiet: {
      type: 'string',
      enum: ['Österreich', 'europaweit', 'weltweit', 'sonstige'],
      title: 'Räumlicher Geltungsbereich',
    },
    gebiet_sonstige: {
      type: 'string',
      maxLength: 200,
      title: 'Sonstige Gebiete',
      description: 'Nur wenn „sonstige“.',
    },
    befristet: { type: 'boolean', title: 'Befristet?' },
    befristung_bis: {
      type: 'string',
      format: 'date',
      title: 'Befristet bis',
    },
    gebuehr_art: {
      type: 'string',
      enum: [
        'einmalige Lizenzgebühr',
        'laufende Lizenzgebühr',
        'umsatzabhängige Lizenzgebühr',
        'sonstige Vergütung',
      ],
      title: 'Art der Lizenzgebühr',
    },
    gebuehr: {
      type: 'string',
      maxLength: 200,
      title: 'Lizenzgebühr (Betrag / %)',
      description: 'Zahlen- oder Prozent-Slot, kein XXXX.',
    },
    zahlungsfrist_tage: {
      type: 'number',
      minimum: 0,
      title: 'Zahlungsfrist (Tage nach Rechnung)',
    },
    unterlizenz: {
      type: 'string',
      enum: ['berechtigt', 'nicht berechtigt'],
      title: 'Unterlizenz',
    },
    ort: { type: 'string', maxLength: 80, title: 'Ort' },
    datum: { type: 'string', format: 'date', title: 'Datum' },
  },
  if: { properties: { befristet: { const: true } }, required: ['befristet'] },
  then: { required: ['befristung_bis'] },
};

export const GERUEST_TEMPLATES: ContractTemplateDef[] = [
  {
    id: 'kauf_verkauf',
    label: 'Kauf / Verkauf',
    blurb: 'Bewegliche Güter: Muster-Klauseln klickbar · Liegenschaft nur Slots',
    maturity: 'geruest',
    sourceMuster: phillipMuster('muster-kaufvertrag.docx'),
    schema: KAUF_SCHEMA,
    blocks: [
      {
        id: 'parteien',
        label: 'Parteien',
        hint: 'Verkäufer und Käufer',
        fieldKeys: ['verkaeufer', 'kaeufer'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'gegenstand',
        label: 'Gegenstand',
        hint: 'Art, Bezeichnung, Lage',
        fieldKeys: ['gegenstand_art', 'gegenstand'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'kaufpreis',
        label: 'Kaufpreis',
        hint: 'Betrag, Worte, Zahlungsart',
        fieldKeys: ['kaufpreis_eur', 'kaufpreis_worten', 'zahlungsart', 'zahlung_bis', 'iban'],
        defaultEnabled: true,
      },
      {
        id: 'uebergabe',
        label: 'Übergabe',
        hint: 'Datum und Ort',
        fieldKeys: ['uebergabe_am', 'uebergabe_ort'],
        defaultEnabled: true,
      },
      {
        id: 'eigentum',
        label: 'Eigentum / Vorbehalt',
        hint: '§ 4',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'gewaehrleistung',
        label: 'Gewährleistung',
        hint: 'Privatverkauf oder Unternehmer',
        fieldKeys: ['gewaehrleistung_variante'],
        defaultEnabled: false,
      },
      {
        id: 'zusicherungen',
        label: 'Zusicherungen',
        hint: '§ 6',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'untersuchung',
        label: 'Untersuchungspflicht',
        hint: '§ 7',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'aufloesung',
        label: 'Vertragsauflösung',
        hint: '§ 8',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'kosten',
        label: 'Kosten / Abgaben',
        hint: 'Wer trägt Kosten',
        fieldKeys: ['kosten_tragung'],
        defaultEnabled: false,
      },
      {
        id: 'datenschutz',
        label: 'Datenschutz',
        hint: '§ 10',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'schluss',
        label: 'Schriftform und Schluss',
        hint: 'Ort, Datum',
        fieldKeys: ['ort', 'datum'],
        defaultEnabled: true,
      },
    ],
  },
  {
    id: 'gesellschaftervertrag',
    label: 'Gesellschaftervertrag',
    blurb: 'GmbH / OG / GesbR / KG / FlexKap · Gerüst, keine ARKO-Vorlage',
    maturity: 'geruest',
    schema: GESELLSCHAFTER_SCHEMA,
    blocks: [
      {
        id: 'gesellschaft',
        label: 'Gesellschaft',
        hint: 'Name und Rechtsform',
        fieldKeys: ['gesellschaft', 'stammkapital_eur'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'gesellschafter',
        label: 'Gesellschafter',
        hint: 'A, B — Plus für C, D, … so viele die Gesellschaft hat',
        fieldKeys: ['gesellschafter_a', 'gesellschafter_b', 'anteil_a_prozent', 'anteil_b_prozent'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'geschaeftsfuehrung',
        label: 'Geschäftsführung',
        hint: MUSTER_FOLGT,
        fieldKeys: ['geschaeftsfuehrung'],
        defaultEnabled: true,
      },
      {
        id: 'nachfolge',
        label: 'Nachfolge / Vinkulierung',
        hint: MUSTER_FOLGT,
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'wettbewerb',
        label: 'Wettbewerb / Abwerbeverbot',
        hint: MUSTER_FOLGT,
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
    ],
    extraSlots: [
      {
        id: 'nachfolge_notiz',
        blockId: 'nachfolge',
        label: 'Nachfolge — Slot',
        kind: 'string',
        placeholder: MUSTER_FOLGT,
      },
      {
        id: 'wettbewerb_notiz',
        blockId: 'wettbewerb',
        label: 'Wettbewerb — Slot',
        kind: 'string',
        placeholder: MUSTER_FOLGT,
      },
    ],
  },
  {
    id: 'innenverhaeltnis',
    label: 'Innenverhältnis-Erklärung',
    blurb: 'Stimmbindung, Weisung, interne Abrede · Gerüst',
    maturity: 'geruest',
    schema: INNEN_SCHEMA,
    blocks: [
      {
        id: 'parteien',
        label: 'Parteien',
        hint: 'Erklärende und Gegenpartei',
        fieldKeys: ['erklaerende', 'gegenpartei'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'gegenstand',
        label: 'Gegenstand',
        hint: 'Stichwort und Datum',
        fieldKeys: ['gegenstand', 'wirksam_ab'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'vollmacht',
        label: 'Vollmacht / Vertretung',
        hint: MUSTER_FOLGT,
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'weisung',
        label: 'Weisungsrecht',
        hint: MUSTER_FOLGT,
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'vertraulichkeit',
        label: 'Vertraulichkeit',
        hint: MUSTER_FOLGT,
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
    ],
    extraSlots: [
      {
        id: 'vollmacht_notiz',
        blockId: 'vollmacht',
        label: 'Vollmacht — Slot',
        kind: 'string',
        placeholder: MUSTER_FOLGT,
      },
      {
        id: 'weisung_notiz',
        blockId: 'weisung',
        label: 'Weisung — Slot',
        kind: 'string',
        placeholder: MUSTER_FOLGT,
      },
      {
        id: 'vertraulichkeit_notiz',
        blockId: 'vertraulichkeit',
        label: 'Vertraulichkeit — Slot',
        kind: 'string',
        placeholder: MUSTER_FOLGT,
      },
    ],
  },
  {
    id: 'pfand_leih',
    label: 'Pfand- / Leihvertrag',
    blurb: 'Pfand: Muster-Klauseln klickbar · Leihe nur Slots',
    maturity: 'geruest',
    sourceMuster: phillipMuster('muster-pfandvertrag.docx'),
    schema: PFAND_LEIH_SCHEMA,
    blocks: [
      {
        id: 'typ_parteien',
        label: 'Art und Parteien',
        hint: 'Pfand oder Leihe',
        fieldKeys: ['vertragstyp', 'geber', 'nehmer'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'praeambel',
        label: 'Präambel',
        hint: '§ 1',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'forderung',
        label: 'Gesicherte Forderung',
        hint: 'Rechtsgrund, Schuldner, Betrag',
        fieldKeys: ['rechtsgrund', 'schuldner', 'forderung_eur'],
        defaultEnabled: true,
      },
      {
        id: 'gegenstand',
        label: 'Pfandgegenstand',
        hint: 'Sache',
        fieldKeys: ['gegenstand', 'gegenstand_beschreibung', 'identifikation', 'schaetzwert_eur'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'bestellung',
        label: 'Pfandbestellung',
        hint: '§ 4',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'dauer',
        label: 'Übergabe / Dauer',
        hint: 'Übergabe; Leihe optional befristet',
        fieldKeys: ['beginn', 'befristet', 'ende'],
        defaultEnabled: true,
      },
      {
        id: 'verwahrung',
        label: 'Verwahrung',
        hint: '§ 6',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'erhaltung',
        label: 'Erhaltung',
        hint: '§ 7',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'verwertung',
        label: 'Verwertung',
        hint: '§ 8',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'rueckgabe',
        label: 'Rückgabe',
        hint: '§ 9',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'gewaehrleistungen',
        label: 'Gewährleistungen',
        hint: '§ 10',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'haftung',
        label: 'Haftung',
        hint: '§ 11',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'kosten',
        label: 'Kosten',
        hint: 'Wer trägt Kosten',
        fieldKeys: ['kosten_tragung'],
        defaultEnabled: false,
      },
      {
        id: 'schluss',
        label: 'Schriftform und Schluss',
        hint: 'Ort, Datum',
        fieldKeys: ['ort', 'datum'],
        defaultEnabled: true,
      },
    ],
  },
  {
    id: 'servitut_raeumung',
    label: 'Servitut / Räumung',
    blurb: 'Servitut oder Räumungsvertrag · Gerüst, keine ARKO-Vorlage',
    maturity: 'geruest',
    schema: SERVITUT_SCHEMA,
    blocks: [
      {
        id: 'typ_parteien',
        label: 'Art und Parteien',
        hint: 'Servitut oder Räumung',
        fieldKeys: ['vertragstyp', 'berechtigte', 'verpflichtete'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'liegenschaft',
        label: 'Liegenschaft',
        hint: 'Adresse, optional EZ/KG',
        fieldKeys: ['liegenschaft'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'umfang',
        label: 'Umfang / Inhalt',
        hint: MUSTER_FOLGT,
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'dauer',
        label: 'Dauer / Befristung',
        hint: MUSTER_FOLGT,
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
    ],
    extraSlots: [
      {
        id: 'umfang_notiz',
        blockId: 'umfang',
        label: 'Umfang — Slot',
        kind: 'string',
        placeholder: MUSTER_FOLGT,
      },
      {
        id: 'dauer_notiz',
        blockId: 'dauer',
        label: 'Dauer — Slot',
        kind: 'string',
        placeholder: MUSTER_FOLGT,
      },
    ],
  },
  {
    id: 'arbeitsvertrag',
    label: 'Arbeitsvertrag',
    blurb: 'Muster-Klauseln klickbar · Probezeit / KV / Freistellung / Überstunden optional',
    maturity: 'geruest',
    sourceMuster: phillipMuster('muster-arbeitsvertrag.docx'),
    schema: ARBEIT_SCHEMA,
    blocks: [
      {
        id: 'parteien',
        label: 'Parteien',
        hint: 'Dienstgeber und Dienstnehmer',
        fieldKeys: ['dienstgeber', 'dienstnehmer'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'vertragsabschluss',
        label: 'Vertragsabschluss',
        hint: '§ 1',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'beginn',
        label: 'Beginn',
        hint: 'Datum, optional Befristung',
        fieldKeys: ['beginn', 'befristet', 'befristung_bis'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'taetigkeit',
        label: 'Tätigkeit',
        hint: 'Position und Aufgaben-Slot',
        fieldKeys: ['position', 'aufgaben'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'dienstort',
        label: 'Dienstort',
        hint: 'Arbeitsort',
        fieldKeys: ['dienstort'],
        defaultEnabled: true,
      },
      {
        id: 'arbeitszeit',
        label: 'Arbeitszeit',
        hint: 'Stunden / Woche',
        fieldKeys: ['wochenstunden'],
        defaultEnabled: true,
      },
      {
        id: 'ueberstunden',
        label: 'Überstunden',
        hint: 'Nur die Klausel an/aus; Dokumentation und Gegenzeichnung immer',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'entgelt',
        label: 'Entgelt',
        hint: 'Brutto, Termin, IBAN',
        fieldKeys: ['brutto_eur', 'zahlungstermin', 'iban'],
        defaultEnabled: true,
      },
      {
        id: 'kollektivvertrag',
        label: 'Kollektivvertrag',
        hint: 'Optionaler Baustein',
        fieldKeys: ['kollektivvertrag'],
        defaultEnabled: false,
      },
      {
        id: 'ueberzahlung',
        label: 'Überzahlung',
        hint: 'KV-Brutto + Überzahlung (Notiz)',
        fieldKeys: ['kv_vollzeit_brutto', 'ueberzahlung_eur'],
        defaultEnabled: false,
      },
      {
        id: 'probezeit',
        label: 'Probezeit',
        hint: 'Optionaler Baustein',
        fieldKeys: ['probezeit_dauer'],
        defaultEnabled: false,
      },
      {
        id: 'urlaub',
        label: 'Urlaub',
        hint: '§ 9',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'krankheit',
        label: 'Krankheit',
        hint: '§ 10',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'verschwiegenheit',
        label: 'Verschwiegenheit',
        hint: '§ 11',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'datenschutz',
        label: 'Datenschutz',
        hint: '§ 12',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'nebentatigkeit',
        label: 'Nebentätigkeit',
        hint: '§ 13',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'beendigung',
        label: 'Beendigung',
        hint: 'Ohne Ausrufezeichen-Notiz',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'kuendigungsfrist',
        label: 'Kündigungsfrist',
        hint: 'Notiz: Monate, schriftlich, Mitte/Ende',
        fieldKeys: ['kuendigungsfrist_monate'],
        defaultEnabled: false,
      },
      {
        id: 'freistellung',
        label: 'Freistellung',
        hint: 'Dienstnehmer vom Dienst — nicht den Arbeitgeber',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'rueckgabe',
        label: 'Rückgabe',
        hint: '§ 15',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'schluss',
        label: 'Schriftform und Schluss',
        hint: 'AVRAG, Ort/Datum',
        fieldKeys: ['ort_datum'],
        defaultEnabled: true,
      },
    ],
  },
  {
    id: 'darlehen',
    label: 'Darlehensvertrag',
    blurb: 'Muster-Klauseln klickbar · Währung und Worte als Slots',
    maturity: 'geruest',
    sourceMuster: phillipMuster('muster-darlehensvertrag.docx'),
    schema: DARLEHEN_SCHEMA,
    blocks: [
      {
        id: 'parteien',
        label: 'Parteien',
        hint: 'Darlehensgeber und -nehmer',
        fieldKeys: ['darlehensgeber', 'darlehensnehmer'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'betrag_laufzeit',
        label: 'Betrag und Laufzeit',
        hint: 'Währung, Betrag, Worte, von/bis',
        fieldKeys: ['waehrung', 'betrag_eur', 'betrag_worten', 'laufzeit_von', 'laufzeit_bis'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'auszahlung',
        label: 'Auszahlung',
        hint: 'Überweisung oder Bar',
        fieldKeys: ['auszahlungsart', 'auszahlung_bis', 'iban'],
        defaultEnabled: true,
      },
      {
        id: 'zins_rueck',
        label: 'Zins und Rückzahlung',
        hint: 'Varianten als Felder',
        fieldKeys: [
          'verzinsung',
          'zinssatz_prozent',
          'index_notiz',
          'zins_faelligkeit',
          'rueckzahlung',
          'raten_anzahl',
          'rate_eur',
          'rate_faelligkeit',
          'rueck_iban',
        ],
        defaultEnabled: true,
      },
      {
        id: 'vorzeitig',
        label: 'Vorzeitige Rückzahlung',
        hint: '§ 6',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'verzug',
        label: 'Verzug',
        hint: '§ 7',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'sicherheit',
        label: 'Sicherheiten',
        hint: 'keine / Bürgschaft / Pfand / sonstige',
        fieldKeys: ['sicherheit_art', 'sicherheit_notiz'],
        defaultEnabled: false,
      },
      {
        id: 'kuendigung',
        label: 'Kündigung',
        hint: '§ 9',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'aufrechnung',
        label: 'Aufrechnung',
        hint: '§ 10',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'schluss',
        label: 'Schriftform und Schluss',
        hint: 'Ort, Datum',
        fieldKeys: ['ort', 'datum'],
        defaultEnabled: true,
      },
    ],
  },
  {
    id: 'lizenz',
    label: 'Lizenzvertrag',
    blurb: 'Muster-Klauseln klickbar · Gegenstände als leere Slots',
    maturity: 'geruest',
    sourceMuster: phillipMuster('muster-lizenzvertrag.docx'),
    schema: LIZENZ_SCHEMA,
    blocks: [
      {
        id: 'parteien',
        label: 'Parteien',
        hint: 'Lizenzgeber und -nehmer',
        fieldKeys: ['lizenzgeber', 'lizenznehmer'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'gegenstand',
        label: 'Gegenstand und Art',
        hint: 'Lizenzgegenstand, exklusiv/einfach',
        fieldKeys: ['gegenstand', 'art'],
        defaultEnabled: true,
        locked: true,
      },
      {
        id: 'umfang',
        label: 'Nutzungsumfang',
        hint: 'Rechte und Zwecke',
        fieldKeys: ['nutzungsumfang', 'zwecke'],
        defaultEnabled: true,
      },
      {
        id: 'geltung',
        label: 'Geltung',
        hint: 'Gebiet, Zeit, Gebühr',
        fieldKeys: [
          'gebiet',
          'gebiet_sonstige',
          'befristet',
          'befristung_bis',
          'gebuehr_art',
          'gebuehr',
          'zahlungsfrist_tage',
        ],
        defaultEnabled: true,
      },
      {
        id: 'eigentum',
        label: 'Eigentum an Rechten',
        hint: '§ 7',
        fieldKeys: [],
        defaultEnabled: true,
        extraOnly: true,
      },
      {
        id: 'unterlizenz',
        label: 'Unterlizenz',
        hint: 'berechtigt / nicht',
        fieldKeys: ['unterlizenz'],
        defaultEnabled: false,
      },
      {
        id: 'gewaehrleistung',
        label: 'Gewährleistung',
        hint: '§ 9',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'schutzrechte',
        label: 'Schutzrechte',
        hint: '§ 10',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'vertraulichkeit',
        label: 'Vertraulichkeit',
        hint: '§ 11',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'haftung',
        label: 'Haftung',
        hint: '§ 12',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'beendigung',
        label: 'Beendigung',
        hint: '§ 13',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'loeschung',
        label: 'Rückgabe / Löschung',
        hint: '§ 14',
        fieldKeys: [],
        defaultEnabled: false,
        extraOnly: true,
      },
      {
        id: 'schluss',
        label: 'Schriftform und Schluss',
        hint: 'Ort, Datum',
        fieldKeys: ['ort', 'datum'],
        defaultEnabled: true,
      },
    ],
  },
];
